import React, { useState, useEffect } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Container, FormControlLabel, Grid, Checkbox, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Slide, TextField } from '@mui/material';

import { useRecoilValue } from 'recoil';
import { useRelocateAddressCallback, useUpdateAddressCallback, useAddAccessCodeCallback, useVerifyAddressCallBack, selectedAddressState, verifiedPhotoState } from '../../states/address';
import { useResolveCallback, incidentDetailState, useGotoListPageCallback } from '../../states/incidents';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { H4, Label } from '../../components/Heading';
import { BlueBox, ColorBox } from '../../components/Box';
import Dev from '../Dev';
import GpsLocation from '../GpsLocation';
import EditAddressLocationDialog from '../EditAddressLocationDialog';
import moment from 'moment';
import toast from 'react-hot-toast';
import Verify from './verify';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function AddressDetail({ shipment }) {
  const address = useRecoilValue(selectedAddressState);
  const [open, setOpen] = useState(false);
  const relocateAddress = useRelocateAddressCallback();
  const verifyAddress = useVerifyAddressCallBack();
  const updateAddress = useUpdateAddressCallback();
  const addAccessCode = useAddAccessCodeCallback();
  const [uncharted, setUncharted] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [suggested, setSuggested] = useState();
  const [editingAccessCode, setEditingAccessCode] = useState('');
  const [openEditingAccessCode, setOpenEditingAccessCode] = useState(false);
  const verifiedPhoto = useRecoilValue(verifiedPhotoState);
  const selectedIncident = useRecoilValue(incidentDetailState);
  const resolve = useResolveCallback();
  const gotoList = useGotoListPageCallback();
  useEffect(() => {
    setUncharted((address?.uncharted) || false);
    setPinVerified((address?.pin_verified) || false);
  }, [address]);

  const relocate = () => {
    relocateAddress();
  };

  const verifyPin = () => {
    verifyAddress();
  };

  const updateUncharted = (e, b) => {
    setUncharted(b);
    updateAddress({ uncharted: b });
  };

  const updatePinVerified = (e, b) => {
    setPinVerified(b);
    updateAddress({ pin_verified: b, photos_verified: verifiedPhoto, shipment_id: shipment?.id });
  };

  const openEditDialog = () => {
    setSuggested(address?.location?.latitude ? `${address.location.latitude},${address.location.longitude}` : null);
    setOpen(true);
  };

  const openEditAccessCode = () => {
    setEditingAccessCode('');
    setOpenEditingAccessCode(true);
  };
  const saveAccessCode = async () => {
    if (editingAccessCode) {
      try {
        await addAccessCode(editingAccessCode, shipment?.id);
        if (selectedIncident && selectedIncident.type !== 'WRONG-DROPOFF-LOCATION') {
          await resolve('RESOLVED');
          gotoList();
        }
        setOpenEditingAccessCode(false);
      } catch (error) {
        toast.error('Failed to save access code. Please try again.');
      }
    } else {
      setOpenEditingAccessCode(false);
    }
  };

  const accuracyColor = (s) => (s === 'UNLOCATABLE' ? '#f34' : s === 'MANUAL' ? '#34F' : '#3f4');

  if (!address) return <div></div>;
  return (
    <BlueBox>
      <Container style={{ padding: 0 }}>
        <Dev>
          <Typography style={{ fontSize: '0.8em' }}>{address.id}</Typography>
        </Dev>
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ paddingTop: '5px' }}>{address.accuracy && <code style={{ color: accuracyColor(address.accuracy) }}>{address.accuracy}</code>}</div>
          <div style={{ position: 'absolute', top: 0, right: 0 }}>
            {address.accuracy === 'UNLOCATABLE' && (
              <Dev>
                <Button size="small" onClick={relocate}>
                  relocate
                </Button>
              </Dev>
            )}
            <Button size="small" onClick={openEditDialog}>
              edit
            </Button>
          </div>
        </div>
        <EditAddressLocationDialog
          open={open}
          onClose={() => setOpen(false)}
          suggested={suggested}
          origin={suggested}
          shouldUncharted={address.uncharted}
          shipmentId={shipment?.id}
        />
      </Container>
      {address.geocoding_addresses && (
        <Dev>
          <Typography style={{ fontSize: '0.8em' }}>Geocoding address: {address.geocoding_addresses.join(',')}</Typography>
        </Dev>
      )}
      {address.location && (
        <Container style={{ padding: 0 }}>
          <GpsLocation lat={address.location.latitude} lng={address.location.longitude} google={true} />
          <Grid container>
            <Grid size={6}>
              <div style={{ paddingTop: '10px' }}>
                <div style={{ width: '140px' }}>
                  <FormControlLabel
                    style={{ marginLeft: 0 }}
                    control={uncharted ? <CheckIcon style={{ color: 'green', marginRight: '4px' }} fontSize="small" /> : <ClearIcon style={{ color: '#f44', marginRight: '4px' }} fontSize="small" />}
                    label="Un-Charted"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Typography style={{ color: '#888', fontSize: '0.8em' }}>Use GPS location for navigation</Typography>
                </div>
              </div>
            </Grid>
            <Grid size={6}>
              <div style={{ paddingTop: '10px' }}>
                <div style={{ width: '140px' }}>
                  <FormControlLabel
                    style={{ marginLeft: 0 }}
                    control={pinVerified ? <CheckIcon style={{ color: 'green', marginRight: '4px' }} fontSize="small" /> : <ClearIcon style={{ color: '#f44', marginRight: '4px' }} fontSize="small" />}
                    label="Verified Pin"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Typography style={{ color: '#888', fontSize: '0.8em' }}>
                    Dropoff pin is <strong>accurate</strong>
                  </Typography>
                </div>
                <Dev>
                  {!pinVerified && (
                    <div>
                      <Button size="small" onClick={verifyPin}>
                        Verify
                      </Button>
                    </div>
                  )}
                </Dev>
              </div>
            </Grid>
          </Grid>
        </Container>
      )}

      <Verify address={address}></Verify>

      <Divider style={{ marginTop: '10px' }} />
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: 3, right: 3 }}>
          <Button size="small" onClick={openEditAccessCode}>
            ADD
          </Button>
        </div>
        <H4>Access Code: {address.access_code && <code>{address.access_code}</code>}</H4>
        {address.known_access_codes && (
          <ColorBox>
            <Grid container>
              <Grid size={5}>
                <H4>Date</H4>
              </Grid>
              <Grid size={4}>
                <H4>Code</H4>
              </Grid>
              <Grid size={3}>
                <H4>Source</H4>
              </Grid>
            </Grid>
            {address.known_access_codes.map((code) => (
              <Grid container key={code.created}>
                <Grid size={5} style={{ fontSize: '0.9em' }}>
                  {moment(code.created).format('MM/DD/YY HH:mm')}
                </Grid>
                <Grid size={4}>
                  {code.code}
                </Grid>
                <Grid size={3}>
                  {code.source}
                </Grid>
              </Grid>
            ))}
          </ColorBox>
        )}
        <Dialog
          maxWidth="md"
          open={openEditingAccessCode}
          // TransitionComponent={Transition}
          keepMounted
          onClose={() => setOpenEditingAccessCode(false)}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle>Add Access Code</DialogTitle>
          <DialogContent>
            <div style={{ width: '480px' }}>
              <TextField placeholder="Access Code" value={editingAccessCode} fullWidth variant="outlined" onChange={(e) => setEditingAccessCode(e.target.value || '')} />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={saveAccessCode}>Save And Resolve</Button>
            <Button onClick={() => setOpenEditingAccessCode(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </div>
    </BlueBox>
  );
}

export default AddressDetail;
